import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@clerk/nextjs";
import SearchHistoryService from "../../lib/searchHistoryService";

// Custom hook for efficient search functionality
export const useSearch = (initialParams = {}) => {
  const { isSignedIn, isLoaded } = useAuth();
  const [searchParams, setSearchParams] = useState({
    query: "",
    type: "",
    location: "",
    priceRange: "",
    ...initialParams,
  });

  const [results, setResults] = useState([]);
  const [fallbackResults, setFallbackResults] = useState([]);
  const [searchMeta, setSearchMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);

  // Debounce timer ref (not used anymore - keeping for cleanup)
  const debounceTimer = useRef(null);
  const abortController = useRef(null);

  // Cleanup function for any remaining timers
  const cleanupTimers = useCallback(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
  }, []);

  // Main search function
  const performSearch = useCallback(
    async (params = searchParams, saveToHistory = true) => {
      // Cancel previous request
      if (abortController.current) {
        abortController.current.abort();
      }

      // Create new abort controller
      abortController.current = new AbortController();

      setLoading(true);
      setError(null);

      try {
        // Build query string from parameters
        const queryParams = new URLSearchParams();

        if (params.query?.trim())
          queryParams.append("query", params.query.trim());
        if (params.type) queryParams.append("type", params.type);
        if (params.location) queryParams.append("location", params.location);

        // Handle price range
        if (params.priceRange) {
          const [minPrice, maxPrice] = params.priceRange.split("-");
          if (minPrice && minPrice !== "0")
            queryParams.append("minPrice", minPrice);
          if (maxPrice && maxPrice !== "")
            queryParams.append("maxPrice", maxPrice);
        }

        queryParams.append("limit", "12");
        queryParams.append("page", "1");

        const response = await fetch(
          `/api/listings/search?${queryParams.toString()}`,
          {
            signal: abortController.current.signal,
          }
        );

        if (!response.ok) {
          let errorMessage = `Search failed: ${response.status}`;

          if (response.status === 503) {
            errorMessage =
              "Search service is temporarily unavailable. Please try again in a moment.";
          } else if (response.status === 500) {
            errorMessage = "Internal server error. Our team has been notified.";
          } else if (response.status === 404) {
            errorMessage =
              "Search endpoint not found. Please refresh the page.";
          } else if (response.status >= 400 && response.status < 500) {
            errorMessage =
              "Invalid search request. Please check your search terms.";
          }

          throw new Error(errorMessage);
        }

        const data = await response.json();

        if (data.success) {
          setResults(data.data.listings || []);
          setFallbackResults(data.data.fallbackListings || []);
          setSearchMeta(data.data.searchMeta || null);
          setPagination(data.data.pagination);

          // Save search to history only if there's a meaningful search query or filters
          const hasSearchCriteria =
            (params.query && params.query.trim().length >= 2) ||
            params.type ||
            params.location ||
            params.priceRange;

          console.log("Search completed. Checking if should save to history:", {
            hasSearchCriteria,
            query: params.query,
            queryLength: params.query?.trim().length,
            type: params.type,
            location: params.location,
            priceRange: params.priceRange,
            resultsCount: data.data.listings?.length || 0,
          });

          if (hasSearchCriteria && saveToHistory && isSignedIn && isLoaded) {
            console.log("Saving search to history for authenticated user...");

            // Add a small delay to ensure authentication context is fully established
            setTimeout(() => {
              SearchHistoryService.saveSearch({
                searchQuery: params.query || "",
                filters: {
                  type: params.type || "",
                  location: params.location || "",
                  priceRange: params.priceRange || "",
                },
                resultsCount: data.data.listings?.length || 0,
              })
                .then((response) => {
                  console.log("Search history saved successfully:", response);
                })
                .catch((error) => {
                  console.log("Failed to save search history:", error);
                  // Don't throw - this is non-critical
                });
            }, 1000); // Wait 1 second for authentication context to be ready
          } else if (hasSearchCriteria && !saveToHistory) {
            console.log(
              "Skipping search history save - saveToHistory is false"
            );
          } else if (hasSearchCriteria && !isSignedIn) {
            console.log("Skipping search history save - user not signed in");
          } else if (hasSearchCriteria && !isLoaded) {
            console.log("Skipping search history save - auth not loaded yet");
          } else {
            console.log(
              "Not saving search to history - no meaningful search criteria"
            );
          }
        } else {
          throw new Error(data.error || "Search failed");
        }
      } catch (error) {
        if (error.name === "AbortError") {
          console.log("Search request was cancelled");
          return;
        }
        console.error("Search error:", error);
        setError(error.message);
        setResults([]);
        setFallbackResults([]);
        setSearchMeta(null);
        setPagination(null);
      } finally {
        setLoading(false);
      }
    },
    [searchParams, isLoaded, isSignedIn]
  );

  // Update search parameters
  const updateSearchParams = useCallback((newParams) => {
    setSearchParams((prev) => {
      const updated = { ...prev, ...newParams };

      // Don't trigger automatic search anymore
      // User must click search button to perform search

      return updated;
    });
  }, []);

  // Load more results (pagination)
  const loadMore = useCallback(
    async (page) => {
      if (!pagination?.hasNextPage || loading) return;

      setLoading(true);
      try {
        const queryParams = new URLSearchParams();

        if (searchParams.query?.trim())
          queryParams.append("query", searchParams.query.trim());
        if (searchParams.type) queryParams.append("type", searchParams.type);
        if (searchParams.location)
          queryParams.append("location", searchParams.location);

        if (searchParams.priceRange) {
          const [minPrice, maxPrice] = searchParams.priceRange.split("-");
          if (minPrice && minPrice !== "0")
            queryParams.append("minPrice", minPrice);
          if (maxPrice && maxPrice !== "+")
            queryParams.append("maxPrice", maxPrice);
        }

        queryParams.append("limit", "12");
        queryParams.append("page", page.toString());

        const response = await fetch(
          `/api/listings/search?${queryParams.toString()}`
        );
        const data = await response.json();

        if (data.success) {
          setResults((prev) => [...prev, ...data.data.listings]);
          setPagination(data.data.pagination);
        }
      } catch (error) {
        console.error("Load more error:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    },
    [searchParams, pagination, loading]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      if (abortController.current) {
        abortController.current.abort();
      }
    };
  }, []);

  // Cleanup effect to prevent memory leaks and unwanted searches
  useEffect(() => {
    return () => {
      // Cleanup on unmount
      cleanupTimers();
      if (abortController.current) {
        abortController.current.abort();
      }
    };
  }, [cleanupTimers]);

  return {
    searchParams,
    updateSearchParams,
    results,
    fallbackResults,
    searchMeta,
    loading,
    error,
    pagination,
    performSearch,
    loadMore,
  };
};
